/**
 * Utility functions for applying organization branding colors to charts
 */

/**
 * Converts a hex color to RGB values
 * @param {string} hex - Hex color (e.g., '#FF5733')
 * @returns {object} RGB object with r, g, b properties
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Converts RGB values to hex color
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} Hex color
 */
function rgbToHex(r, g, b) {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}

/**
 * Lightens or darkens a color by a percentage
 * @param {string} hex - Hex color
 * @param {number} percent - Percentage to lighten (positive) or darken (negative)
 * @returns {string} Modified hex color
 */
function adjustColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const adjust = (value) => {
    const adjusted = value + (percent / 100) * 255;
    return Math.max(0, Math.min(255, adjusted));
  };

  return rgbToHex(adjust(rgb.r), adjust(rgb.g), adjust(rgb.b));
}

/**
 * Generates an array of shades/tints for a given color
 * @param {string} baseColor - Base hex color
 * @param {number} count - Number of color variations to generate (default: 7)
 * @returns {string[]} Array of hex colors
 */
function generateShades(baseColor, count = 7) {
  if (!baseColor) return [];

  const shades = [];
  const step = 60 / (count - 1); // Range from -30% to +30%

  for (let i = 0; i < count; i++) {
    const percent = -30 + step * i;
    shades.push(adjustColor(baseColor, percent));
  }

  return shades;
}

/**
 * Generates extended color palette with shades and tints
 * @param {string[]} baseColors - Array of base brand colors
 * @param {number} targetCount - Target number of colors to generate (default: 20)
 * @returns {string[]} Extended array of colors including variations
 */
function generateExtendedPalette(baseColors, targetCount = 20) {
  if (!baseColors || baseColors.length === 0) return [];

  const extendedColors = [...baseColors]; // Start with original colors

  // If we already have enough colors, return them
  if (extendedColors.length >= targetCount) {
    return extendedColors;
  }

  // Generate lighter variations (+15%, +25%)
  const lighterVariations = [15, 25];
  for (const percent of lighterVariations) {
    for (const baseColor of baseColors) {
      if (extendedColors.length >= targetCount) break;
      extendedColors.push(adjustColor(baseColor, percent));
    }
    if (extendedColors.length >= targetCount) break;
  }

  // Generate darker variations (-15%, -25%)
  const darkerVariations = [-15, -25];
  for (const percent of darkerVariations) {
    for (const baseColor of baseColors) {
      if (extendedColors.length >= targetCount) break;
      extendedColors.push(adjustColor(baseColor, percent));
    }
    if (extendedColors.length >= targetCount) break;
  }

  return extendedColors;
}

/**
 * Gets the brand colors palette based on branding configuration
 * @param {object} municipalitySettings - Municipality settings containing color_1 through color_5
 * @param {string} brandingType - Type of branding ('all_colors', 'single_color', 'shades', or null)
 * @param {string} brandingColor - Which color slot to use ('color_1', 'color_2', etc.) - only for single_color/shades
 * @returns {string[]|null} Array of colors to use, or null to use default Highcharts colors
 */
export function getChartBrandingColors(municipalitySettings, brandingType, brandingColor) {
  if (!municipalitySettings) {
    return null;
  }

  // Collect all available brand colors
  const allBrandColors = [
    municipalitySettings.color_1,
    municipalitySettings.color_2,
    municipalitySettings.color_3,
    municipalitySettings.color_4,
    municipalitySettings.color_5,
  ].filter(Boolean); // Remove null/undefined values

  if (allBrandColors.length === 0) {
    return null; // No brand colors configured - fall back to default Highcharts colors
  }

  // When a chart has no explicit branding, automatically use the municipality's brand palette
  // instead of Highcharts' default multi-color ("half and half") scheme.
  switch (brandingType || 'all_colors') {
    case 'all_colors':
      // Use all available brand colors, then generate shades/tints for additional colors
      return generateExtendedPalette(allBrandColors, 20);

    case 'single_color': {
      // Use a single color (brand color or gray) - repeat it to cover all series
      const color = brandingColor === 'gray' ? '#808080' : municipalitySettings[brandingColor];
      return color ? Array(20).fill(color) : null;
    }

    case 'shades': {
      // Generate shades/tints of a single color (brand color or gray)
      const color = brandingColor === 'gray' ? '#808080' : municipalitySettings[brandingColor];
      return color ? generateShades(color, 7) : null;
    }

    default:
      return null;
  }
}

/**
 * Gets a descriptive label for the branding configuration
 * @param {string} brandingType - Type of branding
 * @param {string} brandingColor - Which color slot
 * @returns {string} Human-readable label
 */
export function getBrandingLabel(brandingType, brandingColor) {
  if (!brandingType) {
    return 'Default colors';
  }

  switch (brandingType) {
    case 'all_colors':
      return 'All brand colors';
    case 'single_color':
      return `Single color (${brandingColor})`;
    case 'shades':
      return `Color shades (${brandingColor})`;
    default:
      return 'Default colors';
  }
}
